import { getDealRevenue, getDealPercentage, getDealInstallmentCents } from '../../../../../shared/utils/dealRevenue';
import { parseEuroToCents } from '../../../../../shared/utils/installments';
import React from 'react';
import EntityLinkButton from '../../../../../shared/components/linked-entities/EntityLinkButton';
import { ChevronRight } from '../../../../../shared/assets/AppIcons';

const moneyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
});

function formatMoney(value) {
  if (value === '' || value === null || value === undefined) return '—';
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? moneyFormatter.format(numericValue) : '—';
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function Section({ title, className = '', children }) {
  return (
    <section className={`deal-sheet__section ${className}`.trim()} aria-label={title}>
      <h3 className="deal-sheet__section-title">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, children, wide = false, linked = false }) {
  return (
    <div className={`deal-sheet__field${wide ? ' deal-sheet__field--wide' : ''}${linked ? ' deal-sheet__field--linked' : ''}`}>
      <dt>{label}</dt>
      <dd className="select-text">{children || '—'}</dd>
    </div>
  );
}

// Accounting identifies installments as "1/10", "2/10", etc.; a single receipt has no index.
function isInstallmentPaid(payments, index, count, amountCents) {
  if (!Number.isSafeInteger(amountCents) || amountCents < 0) return false;
  const received = payments.filter((payment) => {
    const installment = String(payment.installment || '').trim();
    const match = installment.match(/^(\d+)(?:\/\d+)?$/);
    return payment.status === 'incassato' && (match ? Number(match[1]) === index + 1 : !installment && count === 1);
  });
  return received.length > 0 && received.reduce((sum, payment) => sum + (parseEuroToCents(payment.amount) ?? 0), 0) >= amountCents;
}

export default function DealReadOnlyView({
  deal,
  dealType,
  staff = [],
  onNavigateClient,
  onNavigateStaff,
  payments = [],
}) {
  const responsible = staff.find((member) => String(member.id) === String(deal.staffId));
  const responsibleName = responsible?.name || deal.assignee || '';
  const responsibleMember = responsible || (deal.staffId && responsibleName ? { id: deal.staffId, name: responsibleName } : null);
  const canNavigateStaff = Boolean(responsibleMember && onNavigateStaff);
  const team = (deal.teamCoinvolto || []).filter(Boolean);
  const proposal = dealType === 'proposta';
  const completed = dealType === 'completato';
  const createdDate = proposal ? deal.proposalDate || deal.dataInizio : deal.dataInizio;
  const dueDate = proposal ? deal.dataPagamentoPrevista : completed ? deal.completedDate || deal.dataPagamento : deal.dataPagamento;
  const clientName = deal.clientResolvedName || deal.client || deal.clientLegacyName || '';
  // Keep the original indexes: an empty date must not shift amounts or paid statuses to another installment.
  const installmentDates = deal.dilazionamento?.scadenze || [];
  const installmentAmounts = deal.dilazionamento?.importiRate?.length ? getDealInstallmentCents(deal) : [];
  const legacyAmount = deal.dilazionamento?.importoRata != null && deal.dilazionamento.importoRata !== ''
    ? getDealRevenue({ value: deal.dilazionamento.importoRata, probability: deal.probability }) : null;
  const hasInstallments = installmentDates.some(Boolean) && (installmentAmounts.length > 0 || legacyAmount !== null);

  return (
    <>
      <Section title="Descrizione" className="deal-sheet__section--description">
        <p className="deal-sheet__description select-text">{deal.description || 'Nessuna descrizione'}</p>
      </Section>

      <Section title="Informazioni">
        <dl className="deal-sheet__field-grid">
          <Field label="Tipo di affare">{deal.tipoAffare}</Field>
          <Field label="Origine">{deal.origineAffare}</Field>
          <Field label={proposal ? 'Creata il' : 'Creato il'}>{formatDate(createdDate)}</Field>
          <Field label={completed ? 'Completato il' : 'Scadenza'}>{formatDate(dueDate)}</Field>
          <Field label="Responsabile" linked={canNavigateStaff}>
            {canNavigateStaff ? (
              <EntityLinkButton onClick={() => onNavigateStaff(responsibleMember.id, responsibleMember)}
                aria-label={`Apri scheda staff di ${responsibleName}`}>
                <span>{responsibleName}</span><ChevronRight size={16} aria-hidden="true" />
              </EntityLinkButton>
            ) : responsibleName || 'Nessun membro assegnato'}
          </Field>
          <Field label="Cliente" linked={Boolean(clientName && onNavigateClient)}>
            {clientName && onNavigateClient ? (
              <EntityLinkButton onClick={onNavigateClient} aria-label={`Apri dettaglio cliente ${clientName}`}>
                <span>{clientName}</span><ChevronRight size={16} aria-hidden="true" />
              </EntityLinkButton>
            ) : clientName || 'Cliente non disponibile'}
          </Field>
          {team.length > 0 ? <Field label="Personale coinvolto" wide>{team.join(' · ')}</Field> : null}
          {deal.clientContact ? <Field label="Contatto" wide>{deal.clientContact}</Field> : null}
          {deal.clientEmail ? <Field label="Email contatto">{deal.clientEmail}</Field> : null}
          {deal.clientPhone ? <Field label="Telefono contatto">{deal.clientPhone}</Field> : null}
        </dl>
      </Section>

      <Section title="Valori incasso e dilazionamento">
        <dl className="deal-sheet__field-grid">
          <Field label="Valore">{formatMoney(deal.value === '' ? deal.estimatedValue : deal.value ?? deal.estimatedValue)}</Field>
          <Field label="Percentuale">{`${getDealPercentage(deal).toLocaleString('it-IT')}%`}</Field>
          <Field label="Incasso aziendale previsto" wide>{formatMoney(getDealRevenue(deal))}</Field>
        </dl>
        <h4 className="deal-sheet__subheading">Dilazionamento</h4>
        {hasInstallments ? (
          <div className="deal-sheet__installments">
            {installmentDates.map((date, index) => {
              const amountCents = installmentAmounts[index] ?? (legacyAmount === null ? null : Math.round(legacyAmount * 100));
              const paid = dealType !== 'proposta' && isInstallmentPaid(payments, index, installmentDates.length, amountCents);
              return (
                <article key={index} className="deal-sheet__installment" aria-label={`Rata ${index + 1}`}>
                  <h5>Rata {index + 1}</h5>
                  <dl>
                    <div><dt>Importo</dt><dd>{formatMoney(amountCents === null ? null : amountCents / 100)}</dd></div>
                    <div><dt>{paid ? 'Stato' : 'Scadenza'}</dt><dd className={paid ? 'deal-sheet__paid' : undefined}>{paid ? 'Saldato' : formatDate(date)}</dd></div>
                  </dl>
                </article>
              );
            })}
          </div>
        ) : <p className="deal-sheet__empty">Nessun piano rateale</p>}
      </Section>

      <Section title="Note">
        <p className="deal-sheet__description select-text">{deal.notes || 'Nessuna nota'}</p>
      </Section>
    </>
  );
}
